import { assign, createMachine } from 'xstate';
import {
  QuestionnaireContext,
  QuestionnaireEventType,
  QuestionnaireStateSchema,
  QuestionnaireEvent,
  QuestionnaireStates,
  UserAnswers,
} from './types';

export const generalHealth = createMachine<QuestionnaireContext, QuestionnaireEventType, QuestionnaireStateSchema>(
  {
    initial: QuestionnaireStates.userInput,
    context: {
      questions: [],
      answers: {},
      previousQuestions: [],
    },
    states: {
      [QuestionnaireStates.userInput]: {
        on: {
          [QuestionnaireEvent.ANSWERED]: {
            target: QuestionnaireStates.loadQuestion,
            actions: ['updateAnswers', 'setSelectedQuestionId'],
          },
          [QuestionnaireEvent.BACK]: {
            target: QuestionnaireStates.userInput,
            cond: 'previousQuestionsExists',
            actions: ['removeLastAnswers', 'updatePreviousQuestions'],
          },
        },
      },
      [QuestionnaireStates.loadQuestion]: {
        always: [
          {
            target: QuestionnaireStates.userInput,
            cond: 'questionExists',
            actions: 'updateCurrentQuestion',
          },
          {
            target: QuestionnaireStates.allQuestionsAnswered,
          },
        ],
      },
      [QuestionnaireStates.allQuestionsAnswered]: {
        type: 'final',
        data: ({ answers }) => answers,
      },
    },
  },
  {
    guards: {
      questionExists: ({ questions, selectedQuestionId }) => questions.some(({ id }) => id === selectedQuestionId),
      previousQuestionsExists: ({ previousQuestions }) => previousQuestions.length > 0,
    },
    actions: {
      removeLastAnswers: assign(({ answers, previousQuestions }) => {
        const tempPreviousQuestions = previousQuestions[previousQuestions.length - 1];
        const newAnswers = Object.keys(answers).reduce<UserAnswers>((savedAnswers, key) => {
          const currentAnswers = savedAnswers;
          if (key !== tempPreviousQuestions.key) {
            currentAnswers[key] = answers[key];
          }
          return currentAnswers;
        }, {});

        return {
          answers: newAnswers,
        };
      }),
      updateCurrentQuestion: assign(({ currentQuestion, previousQuestions, questions, selectedQuestionId }) => ({
        previousQuestions: currentQuestion ? [...previousQuestions, currentQuestion] : previousQuestions,
        currentQuestion: questions.find(({ id }) => id === selectedQuestionId),
      })),
      updatePreviousQuestions: assign(({ previousQuestions }) => ({
        currentQuestion: previousQuestions[previousQuestions.length - 1],
        previousQuestions: previousQuestions.slice(0, previousQuestions.length - 1),
      })),
      updateAnswers: assign(({ answers }, { choices, key, ignoreChoices }) => {
        const mappedAnswers = Array.isArray(choices)
          ? choices.map(({ mappingValue }) => mappingValue).join('|')
          : choices?.selectedValue || choices?.mappingValue;

        return {
          answers: mappedAnswers && !ignoreChoices ? { ...answers, [key]: mappedAnswers } : answers,
        };
      }),
      setSelectedQuestionId: assign((_, { choices }) => ({
        selectedQuestionId: Array.isArray(choices)
          ? choices[0].next.male.nextQuestionId
          : choices?.next.male.nextQuestionId,
      })),
    },
  },
);
